/**
 * Os nove catálogos que classificam um conteúdo.
 *
 * São globais e mudam pouco, então carregam uma vez por sessão (`staleTime`
 * infinito) e daí em diante o filtro é em memória — nenhuma tecla vira
 * requisição. Tamanhos medidos: marcas 73, veículos 1.202, assuntos 43,
 * sistemas 1.663, motores 1.395, nomes de sistema 421, fabricantes 39,
 * produtos 123, origens 5. Somados dão menos que uma página de pinout.
 *
 * **Cascata só de marca para veículo.** No sistema antigo os nove selects se
 * reduzem entre si a cada escolha, o que custa uma consulta por clique. Marca →
 * veículo é hierarquia de verdade e sai de graça, porque o veículo já traz a
 * marca. O resto não reduz: com campo de busca, digitar "ABS" acha mais rápido
 * do que a lista encolher — e quando a combinação não existe, o resultado diz
 * "nenhum conteúdo", que é a resposta honesta.
 */

import { useQuery } from "@tanstack/react-query"
import { obter } from "./cliente"
import { catalogoPorProjeto } from "./query"

export type OpcaoDeCatalogo = {
  id: number
  rotulo: string
  /** Só em veículo e motor: permite a cascata a partir da marca. */
  marcaId?: number
}

type ComNome = { id: number; nome?: string; descricao?: string }
type VeiculoBruto = { id: number; modelo: string; detalhes: string; marcaId: number }
type MotorBruto = { id: number; nome: string; detalhes: string; marcaId: number }
type OrigemBruta = { id: number; descricao?: string; origemDesc?: string }

/**
 * O registro de id 0 é o vazio do cadastro antigo — não é escolha de ninguém.
 * **Produto é a exceção**: ali o id 0 é "Caminhões", um produto de verdade, e
 * filtrá-lo esconderia um produto que existe. Já custou uma correção antes.
 */
const real = (x: { id: number }) => x.id > 0

/**
 * Junta as partes do rótulo sem repetir.
 *
 * O cadastro de veículo guarda `modelo` e `detalhes`, e em boa parte das linhas
 * os dois são o mesmo texto: concatenar dava "Agrale 10000 10000" e "Audi A1
 * A1". Repetição em rótulo não é só feio — ela ocupa a largura que faz o nome
 * seguinte caber, e no meio de 1.202 opções isso atrapalha achar.
 */
function rotulo(...partes: (string | undefined)[]): string {
  const limpas: string[] = []
  for (const p of partes) {
    const t = (p ?? "").trim()
    if (!t) continue
    if (limpas.some((j) => j.toLowerCase() === t.toLowerCase())) continue
    limpas.push(t)
  }
  return limpas.join(" ")
}

function ordenar(itens: OpcaoDeCatalogo[]): OpcaoDeCatalogo[] {
  return [...itens].sort((a, b) => a.rotulo.localeCompare(b.rotulo, "pt-BR"))
}

function usarSimples(chave: string, rota: string, incluirZero = false) {
  const { data, isPending } = useQuery({
    queryKey: ["catalogo-conteudo", chave] as const,
    ...catalogoPorProjeto,
    queryFn: () => obter<ComNome[]>(rota, {}),
  })
  const itens = ordenar((data ?? [])
    .filter((x) => incluirZero || real(x))
    .map((x) => ({ id: x.id, rotulo: (x.nome || x.descricao || "").trim() || `#${x.id}` })))
  return { itens, carregando: isPending }
}

export function usarMarcasDeConteudo() {
  return usarSimples("marcas", "api/marcas")
}

export function usarAssuntos() {
  return usarSimples("assuntos", "api/assuntos")
}

export function usarSistemasDeConteudo() {
  return usarSimples("sistemas", "api/sistemas")
}

export function usarNomesDeSistema() {
  return usarSimples("nome-sistemas", "api/nome-sistemas")
}

export function usarFabricantesDeSistema() {
  return usarSimples("fab-sistemas", "api/fab-sistemas")
}

/** Produto mantém o id 0 — ver a nota de `real`. */
export function usarProdutosDeConteudo() {
  return usarSimples("produtos", "api/produtos", true)
}

export function usarCategoriasDeVeiculo() {
  return usarSimples("categoria-veiculos", "api/categoria-veiculos")
}

export function usarOrigens() {
  const { data, isPending } = useQuery({
    queryKey: ["catalogo-conteudo", "origens"] as const,
    ...catalogoPorProjeto,
    queryFn: () => obter<OrigemBruta[]>("api/origens", {}),
  })
  const itens = ordenar((data ?? []).filter(real).map((o) => ({
    id: o.id,
    rotulo: (o.descricao || o.origemDesc || "").trim() || `#${o.id}`,
  })))
  return { itens, carregando: isPending }
}

/**
 * Veículos com a marca no rótulo e no `marcaId`.
 *
 * A marca no rótulo não é enfeite: são 1.202 veículos numa lista só, e "i30"
 * sem "Hyundai" na frente é ambíguo. Escolher o veículo errado é o tipo de erro
 * que só aparece depois, no conteúdo publicado.
 */
export function usarVeiculosDeConteudo() {
  const marcas = usarMarcasDeConteudo()
  const nome = new Map(marcas.itens.map((m) => [m.id, m.rotulo]))

  const { data, isPending } = useQuery({
    queryKey: ["catalogo-conteudo", "veiculos"] as const,
    ...catalogoPorProjeto,
    queryFn: () => obter<VeiculoBruto[]>("api/veiculos", {}),
  })

  const itens = ordenar((data ?? []).filter(real).map((v) => ({
    id: v.id,
    marcaId: v.marcaId,
    rotulo: rotulo(nome.get(v.marcaId), v.modelo, v.detalhes),
  })))
  return { itens, carregando: isPending || marcas.carregando }
}

export function usarMotoresDeConteudo() {
  const marcas = usarMarcasDeConteudo()
  const nome = new Map(marcas.itens.map((m) => [m.id, m.rotulo]))

  const { data, isPending } = useQuery({
    queryKey: ["catalogo-conteudo", "motores"] as const,
    ...catalogoPorProjeto,
    queryFn: () => obter<MotorBruto[]>("api/motores", {}),
  })

  // Sem marca o rótulo diz isso: na lista filtrada por Honda, "1.5 16V L4 L15A
  // Flex (sem marca)" explica por que ele está ali sem o "Honda" na frente.
  const itens = ordenar((data ?? []).filter(real).map((m) => ({
    id: m.id,
    marcaId: m.marcaId,
    rotulo: rotulo(nome.get(m.marcaId), m.nome || m.detalhes) +
            (m.marcaId > 0 ? "" : " (sem marca)"),
  })))
  return { itens, carregando: isPending || marcas.carregando }
}

/** Todos de uma vez, para a tela não repetir nove chamadas de hook. */
export function usarCatalogosDeConteudo() {
  const marcas = usarMarcasDeConteudo()
  const veiculos = usarVeiculosDeConteudo()
  const assuntos = usarAssuntos()
  const sistemas = usarSistemasDeConteudo()
  const fabricantes = usarFabricantesDeSistema()
  const nomes = usarNomesDeSistema()
  const motores = usarMotoresDeConteudo()
  const produtos = usarProdutosDeConteudo()
  const origens = usarOrigens()

  return {
    marcas, veiculos, assuntos, sistemas, fabricantes, nomes, motores, produtos, origens,
    carregando:
      marcas.carregando || veiculos.carregando || assuntos.carregando ||
      sistemas.carregando || fabricantes.carregando || nomes.carregando ||
      motores.carregando || produtos.carregando || origens.carregando,
  }
}

/**
 * A cascata: com marca escolhida, os veículos (ou motores) dela **e os sem marca**.
 *
 * Um quarto dos motores não tem marca — os genéricos ("1.0", "2.0 16V", "Diesel"),
 * que servem a várias, e alguns específicos que ninguém preencheu (o L15A da
 * Honda, com 3 mil usos). Filtrar só pela marca os escondia de quem escolheu a
 * marca antes, e o motor parecia não existir (feedback #35). Veículo sem marca
 * não há hoje; se aparecer, segue a mesma regra.
 */
export function filtrarPelaMarca(itens: OpcaoDeCatalogo[], marcaId: number): OpcaoDeCatalogo[] {
  return marcaId > 0 ? itens.filter((x) => x.marcaId === marcaId || !x.marcaId) : itens
}
